/**
 * Qué le falta a una inspección de descarga. Una sola lista para dos
 * preguntas que antes tenían su propia copia de la regla:
 *
 *  · ¿está "Completa"? (hasInspectorData en yardInspectionsStore) — tipo de
 *    agua, volumen y las tres fotos;
 *  · ¿se le puede dar "DAR SALIDA MANUAL"? (manualExitMissing) — lo anterior,
 *    más la unidad y, en una pipa sin tag, el paso 1.
 *
 * El backend aplica la misma regla de salida manual
 * (specialInspections.manualExitMissing) y contesta 409 InspectionIncomplete.
 *
 * Módulo PURO a propósito: yardInspectionsStore.ts importa utils/api → firebase
 * y no colecta en un worktree sin apps/v5/.env (CLAUDE.md §Verificación), así
 * que la decisión vive acá y los tests van sobre ésta.
 */

import { PROVIDER_VISIT_FIELDS } from "./specialInspection";

// Evidence titles the inspector must fill for a discharge inspection to count
// as done — the three named photo slots of the "Ver / Completar" modal. Single
// source of truth: the modal builds its slots (icons and all) from this list,
// and deriveInspectionStatus checks against it, so adding a required photo in
// one place cannot leave the other behind. Same three as the backend's
// REQUIRED_PHOTO_TITLES (specialInspections.js).
export const REQUIRED_PHOTO_TITLES = [
  "Nivel de PH",
  "Conductividad",
  "Turbidez",
] as const;

/** Estructural: lo que estas reglas leen de una YardInspection. */
export interface CompletenessFields {
  inspection_type?: string | null;
  special_tag?: string | null;
  registration_completed_at?: unknown;
  vehicle_id?: string | null;
  vehicle_plate?: string | null;
  water_type?: string | null;
  volumen_extraido?: string | number | null;
  evidences?: { title?: string | null }[] | null;
  provider_name?: string | null;
  received_by?: string | null;
  visit_reason?: string | null;
}

const filled = (v: unknown): boolean => String(v ?? "").trim() !== "";

const PROVIDER_FIELD_LABELS: Record<string, string> = {
  provider_name: "Proveedor",
  received_by: "Quién lo recibió",
  visit_reason: "Motivo de la visita",
};

/** Lo que falta de la captura del inspector (tipo de agua, volumen, fotos). */
export function inspectorDataMissing(inspection: CompletenessFields): string[] {
  const missing: string[] = [];
  if (!filled(inspection.water_type)) missing.push("Tipo de agua");
  if (!filled(inspection.volumen_extraido)) missing.push("Volumen extraído");
  const titles = new Set(
    (inspection.evidences ?? []).map((e) => (e.title || "").trim().toLowerCase()),
  );
  for (const title of REQUIRED_PHOTO_TITLES) {
    if (!titles.has(title.toLowerCase())) missing.push(`Foto: ${title}`);
  }
  return missing;
}

/**
 * Lo que le falta a una visita para poder darle "DAR SALIDA MANUAL", en texto
 * para el operador; [] si se puede. La salida manual cierra la visita y levanta
 * la pluma: una descarga sin capturar se iría del patio sin registro.
 *
 * El proveedor legacy (`inspection_type: "provider"` sin tag especial) no
 * descarga y nace "Cancelada": no tiene nada que capturar, así que no se le
 * exige nada — si no, nunca podría salir desde la plataforma.
 */
export function manualExitMissing(inspection: CompletenessFields): string[] {
  if (inspection.inspection_type === "provider" && !inspection.special_tag) return [];
  // Proveedor del tag especial: su "inspección" son sus tres datos, lo que lo
  // pasa a "Completa" (provider_complete). Ni unidad, ni agua, ni fotos, ni tag.
  if (inspection.special_tag === "provider") {
    // Los campos salen de PROVIDER_VISIT_FIELDS, la misma lista que decide
    // provider_complete: un campo nuevo ahí se exige acá sin tocar nada.
    return PROVIDER_VISIT_FIELDS
      .filter(({ field }) => !filled(inspection[field]))
      .map(({ field }) => PROVIDER_FIELD_LABELS[field] ?? field);
  }
  const missing: string[] = [];
  if (inspection.special_tag === "untagged" && !inspection.registration_completed_at) {
    missing.push("Registro de la unidad (paso 1)");
  }
  if (!inspection.vehicle_id && !filled(inspection.vehicle_plate)) missing.push("Unidad");
  return [...missing, ...inspectorDataMissing(inspection)];
}
